import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import PeopleIcon from '@material-ui/icons/People';
import useRoutes from '../../hooks/useRoutes';
import { baseMoment, toAPStyleTime } from '../../helpers/dates';
import Live from '../../assets/Live';
import Agenda from '../../assets/Agenda';
import Time from '../../assets/Time';
import Replay from '../../assets/Replay';
import useStringTransforms from '../../hooks/useStringTransforms';
// import { ACTVITIES_WITH_REPLAY } from '../../helpers/constants';
import CustomPropTypes from '../../helpers/CustomPropTypes';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 130,
    width: '100%',
    backgroundColor: '#E7E7E7',
    color: theme.palette.lightGrey.main,
    borderRadius: 15,
    marginBottom: theme.spacing(2.25),
    padding: theme.spacing(1, 3),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
    '&:last-child': {
      marginBottom: theme.spacing(0),
    },
  },
  clickeable: {
    cursor: 'pointer',
  },
  rootCompact: {
    padding: theme.spacing(1, 1.75),
  },
  liveActivity: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.main,
  },
  replayAvailable: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.darkGrey.main,
  },
  activityNameContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
    },
  },
  activityName: {
    lineHeight: '100%',
    color: 'inherit',
    fontFamily: 'Fancy Cut Pro',
    fontSize: 24,
    [theme.breakpoints.down('sm')]: {
      fontSize: 17,
    },
  },
  statusWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  status: {
    color: 'inherit',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    fontSize: 14,
  },
  statusIcon: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(4),
  },
  dateWrapper: {
    marginTop: theme.spacing(4),
  },
  date: {
    textTransform: 'uppercase',
    marginTop: theme.spacing(0.5),
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
    },
  },
  speakersLine: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  speakers: {
    lineHeight: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 400,
    fontSize: 14,
    color: 'inherit',
  },
  icon: {
    width: 12,
    height: 12,
    marginRight: 4,
    '& path': {
      color: 'inherit',
    },
  },
}));

const ScheduleListItem = ({ activity, compact, allSpeakers }) => {
  const classes = useStyles();
  const {
    name,
    startDate,
    endDate,
    id,
    speakers,
  } = activity;

  const live = baseMoment().isBetween(startDate, endDate, undefined, '[)');
  const replayAvailable = false;
  const theme = useTheme();
  const { goTo } = useRoutes();
  const { toTitleCase } = useStringTransforms();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const workshopSpeakers = speakers?.filter(({ id: speakerId }) => allSpeakers[speakerId]);

  const parseDate = (date) => {
    if (baseMoment(date).minute() === 0) return toAPStyleTime(baseMoment(date).format('hA'));
    return toAPStyleTime(baseMoment(date).format('h.mmA'));
  };

  const onClick = useCallback(() => {
    if (live) goTo('stage.show', { workshopId: id });
    if (replayAvailable) goTo('replay.show', { workshopId: id });
    return null;
  }, [live, replayAvailable, goTo, id]);

  let peopleElement;
  if (workshopSpeakers.length > 1) peopleElement = workshopSpeakers.map(
    ({ id: speakerId }) => allSpeakers[speakerId]?.fullName
  ).join(', ');
  else if (workshopSpeakers.length === 1) {
    const { fullName } = allSpeakers[workshopSpeakers[0].id];
    peopleElement = toTitleCase(fullName);
  }

  return (
    <>
      <div
        className={clsx(
          classes.root, { [classes.replayAvailable]: replayAvailable && !live },
          { [classes.liveActivity]: live }, { [classes.rootCompact]: compact },
          { [classes.clickeable]: (live || replayAvailable) }
        )}
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex={0}
      >
        <div className={classes.activityNameContainer}>
          <Typography variant="body1" className={classes.activityName}>
            {name}
          </Typography>
          <div className={classes.statusWrapper}>
            {live && (
              <>
                <Live className={classes.statusIcon} />
                {!isMobile && (
                  <Typography variant="body1" className={classes.status}>
                    LIVE
                  </Typography>
                )}
              </>
            )}
            {!live && replayAvailable && (
              <>
                <Replay className={classes.statusIcon} />
                {!isMobile && (
                  <Typography variant="body1" className={classes.status}>
                    REPLAY AVAILABLE
                  </Typography>
                )}
              </>
            )}
          </div>
        </div>
        {peopleElement && (
          <div className={classes.speakersLine}>
            <PeopleIcon className={classes.icon} />
            <Typography className={classes.speakers}>
              {peopleElement}
            </Typography>
          </div>
        )}
        <div className={classes.dateWrapper}>
          <Typography variant="caption" className={classes.date}>
            <Time
              className={clsx(
                classes.icon, replayAvailable && classes.replayIcon, live && classes.liveIcon
              )}
            /> <span style={{ textTransform: 'none' }}>{parseDate(startDate)} - {parseDate(endDate)}</span> (GMT +1)
          </Typography>
          <Typography variant="caption" className={classes.date}>
            <Agenda
              className={clsx(
                classes.icon, classes.replayIcon, live && classes.liveIcon
              )}
            /> {baseMoment(startDate).format('dddd, MMMM Do')}
          </Typography>
        </div>
      </div>
    </>
  );
};

ScheduleListItem.propTypes = {
  compact: PropTypes.bool,
  activity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    speakers: PropTypes.arrayOf(
      CustomPropTypes.speaker
    ),
  }).isRequired,
  allSpeakers: PropTypes.objectOf(
    CustomPropTypes.speaker
  ),
};

ScheduleListItem.defaultProps = {
  compact: false,
  allSpeakers: {},
};

const mapStateToProps = ({
  entities: {
    speakers: allSpeakers,
  },
}) => ({
  allSpeakers,
});

export default connect(mapStateToProps, null)(ScheduleListItem);
